import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { TableColumnHeader } from '@/components/ui/table-cells/TableColumnHeader';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { type People } from '@/types';
import { Link, router } from '@inertiajs/react';
import { InlineAccountingCell } from './inline-cells/InlineAccountingCell';
import { InlineCompanyCell } from './inline-cells/InlineCompanyCell';
import { InlineEmailCell } from './inline-cells/InlineEmailCell';
import { InlinePhoneCell } from './inline-cells/InlinePhoneCell';
import { InlinePositionCell } from './inline-cells/InlinePositionCell';

interface PersonTableProps {
    people: People[];
    companies: Array<{ id: string; name: string }>;
    onUpdate?: {
        company?: (personId: string, companyId: string | null) => Promise<void>;
        position?: (personId: string, value: string | null) => Promise<void>;
        email?: (personId: string, value: string | null) => Promise<void>;
        phone?: (personId: string, value: string | null) => Promise<void>;
        accounting?: (personId: string, value: boolean) => Promise<void>;
    };
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc' | null;
    onSortChange?: (column: string, direction: 'asc' | 'desc') => void;
    onColumnHide?: (column: string) => void;
    onColumnShow?: (column: string) => void;
    visibleColumns?: Set<string>;
    columnFilters?: Record<string, string[]>;
    columnFilterOptions?: Record<string, Array<{ value: string; label: string }>>;
    onFilterChange?: (column: string, values: string[]) => void;
    readOnly?: boolean;
}

const DEFAULT_COLUMNS = ['full_name', 'position', 'email', 'phone', 'company', 'accounting'];

export function PersonTable({
    people,
    companies,
    onUpdate,
    sortColumn,
    sortDirection,
    onSortChange,
    onColumnHide,
    visibleColumns = new Set(DEFAULT_COLUMNS),
    columnFilters = {},
    columnFilterOptions = {},
    onFilterChange,
    readOnly = false,
}: PersonTableProps) {
    const { t } = useTranslations();

    const handleRowClick = (personId: string) => {
        router.visit(`/person/${personId}`);
    };

    const handleSort = (column: string, direction: 'asc' | 'desc') => {
        onSortChange?.(column, direction);
    };

    const handleColumnHide = (column: string) => {
        onColumnHide?.(column);
    };

    const allColumns = [
        {
            id: 'full_name',
            label: t('people.index.columns.full_name', 'Full Name'),
            visible: visibleColumns.has('full_name'),
        },
        {
            id: 'position',
            label: t('people.index.columns.position', 'Position'),
            visible: visibleColumns.has('position'),
        },
        {
            id: 'email',
            label: t('people.index.columns.email', 'Email'),
            visible: visibleColumns.has('email'),
        },
        {
            id: 'phone',
            label: t('people.index.columns.phone', 'Phone'),
            visible: visibleColumns.has('phone'),
        },
        {
            id: 'company',
            label: t('people.index.columns.company', 'Company'),
            visible: visibleColumns.has('company'),
        },
        {
            id: 'accounting',
            label: t('people.index.columns.accounting', 'Accounting'),
            visible: visibleColumns.has('accounting'),
        },
    ];

    const columns = allColumns.filter((col) => col.visible);

    return (
        <div className="space-y-4">
            <div className="bg-card mb-4 items-center justify-between gap-3 rounded-xl border px-4 py-2.5">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {columns.map((column) => {
                                const isSorted = sortColumn === column.id;
                                const currentSortDirection = isSorted ? sortDirection : null;
                                const selectedFilters = columnFilters[column.id] || [];

                                return (
                                    <TableHead key={column.id}>
                                        <TableColumnHeader
                                            label={column.label}
                                            sortDirection={currentSortDirection}
                                            onSortAscending={() => handleSort(column.id, 'asc')}
                                            onSortDescending={() => handleSort(column.id, 'desc')}
                                            onHide={() => handleColumnHide(column.id)}
                                            filterOptions={columnFilterOptions[column.id] || []}
                                            selectedFilters={selectedFilters}
                                            onFilterChange={(values) => onFilterChange?.(column.id, values)}
                                            isFiltered={selectedFilters.length > 0}
                                        />
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {people.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="text-muted-foreground py-8 text-center">
                                    {t('people.index.no_people_found', 'No people found.')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            people.map((person) => {
                                return (
                                    <TableRow
                                        key={person.id}
                                        className={cn('cursor-pointer transition-all', 'hover:bg-muted/50')}
                                        onClick={() => handleRowClick(person.id)}
                                    >
                                        {columns.map((column) => {
                                            if (column.id === 'full_name') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <Link
                                                            href={`/person/${person.id}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="hover:bg-accent text-foreground flex w-full items-center gap-2 rounded px-2 py-1.5 text-left font-semibold transition-colors"
                                                        >
                                                            <span>
                                                                {person.first_name} {person.last_name}
                                                            </span>
                                                        </Link>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'position') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlinePositionCell
                                                            value={person.position || null}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.position
                                                                    ? async (value) => {
                                                                          await onUpdate.position!(person.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'email') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineEmailCell
                                                            value={person.email || null}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.email
                                                                    ? async (value) => {
                                                                          await onUpdate.email!(person.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'phone') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlinePhoneCell
                                                            value={person.phone || null}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.phone
                                                                    ? async (value) => {
                                                                          await onUpdate.phone!(person.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'company') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineCompanyCell
                                                            company={person.company || null}
                                                            allCompanies={companies}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.company
                                                                    ? async (companyId) => {
                                                                          await onUpdate.company!(person.id, companyId);
                                                                      }
                                                                    : undefined
                                                            }
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'accounting') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineAccountingCell
                                                            value={person.accounting || false}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.accounting
                                                                    ? async (value) => {
                                                                          await onUpdate.accounting!(person.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            return null;
                                        })}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
